export const OPTIMIZATION_FACTORS = {
  SIZE: 'size',
  WEIGHT: 'weight',
  COST: 'cost',
} as const;

export const DEFAULT_OPTIMIZATION_FACTOR = OPTIMIZATION_FACTORS.SIZE;

export const ValidationErrorType = {
  MISSING_DIMENSIONS: 'MISSING_DIMENSIONS',
  INVALID_DIMENSIONS: 'INVALID_DIMENSIONS',
  NO_ITEMS: 'NO_ITEMS',
  NO_PACKAGES: 'NO_PACKAGES',
} as const;

type ValidationErrorTypeValue = (typeof ValidationErrorType)[keyof typeof ValidationErrorType];

export type BlockingValidationErrorType = typeof ValidationErrorType.MISSING_DIMENSIONS | typeof ValidationErrorType.NO_PACKAGES;

export type ValidationError = {
  type: ValidationErrorTypeValue;
  message: string;
  field: string;
  itemId: string | null;
};

export type ValidationWarning = {
  message: string;
  itemId: string | null;
};

export type ItemWithMissingDimensions = {
  id: string;
  name: string;
  sku: string | undefined;
};

export type ValidationResult = {
  isValid: boolean;
  errors: ValidationError[];
  warnings: ValidationWarning[];
  itemsWithMissingDimensions?: ItemWithMissingDimensions[];
};

export type PackingItem = {
  id: string;
  name: string;
  length: number;
  width: number;
  height: number;
  weight: number;
  fragile: boolean;
  shipAlone: boolean;
};

const FRAGILE_REQUIREMENT = 'fragile';
const SHIP_ALONE_REQUIREMENT = 'ships_own_container';

// Values can arrive raw from CSV import (case/spacing not validated), so normalize before matching.
const normalizePackagingRequirement = (value: string | undefined): string => (value ?? '').trim().toLowerCase().replace(/\s+/g, '_');

export type PackingBox = {
  name: string;
  packageId: string | undefined;
  length: number;
  width: number;
  height: number;
  maxWeight: number;
  tareWeight: number;
  weightBuffer: number;
  price: number;
};

export type TransformOptions = {
  optimizationFactor?: string;
  enabledAlgorithms?: string[];
  sourceDimensionUnit?: string;
  sourceWeightUnit?: string;
};

export type PackingRequest = {
  items: PackingItem[];
  availableBoxes: PackingBox[];
  optimizationFactor: string;
  enabledAlgorithms: string[];
  dimensionUnit: string;
  weightUnit: string;
};

type OrderItem = {
  sku?: string;
  orderItemId?: string;
  fulfillmentPlanItemId?: string;
  name?: string;
  length?: number;
  width?: number;
  height?: number;
  weight?: { value?: number; unit?: string } | number;
  quantity?: number;
  packagingRequirement?: string;
  product?: {
    length?: number;
    width?: number;
    height?: number;
    weight?: number;
  };
};

type Order = {
  orderId?: string;
  items?: OrderItem[];
  orderItems?: OrderItem[];
};

type PackageDimensions = {
  length?: number;
  width?: number;
  height?: number;
};

type WeightValue = { value?: number; unit?: string } | number;

type SellerPackage = {
  packageId?: string;
  packageTypeId?: string;
  name?: string;
  packageTypeName?: string;
  isCustom?: boolean;
  isHidden?: boolean;
  carrierId?: number;
  dimensions?: PackageDimensions;
  length?: number;
  width?: number;
  height?: number;
  innerLength?: number;
  innerWidth?: number;
  innerHeight?: number;
  maxWeight?: WeightValue;
  tareWeight?: WeightValue;
  weightBuffer?: WeightValue;
  weightCapacity?: number;
  weightLimit?: number;
  weightUnit?: string;
  cost?: number;
  price?: number;
};

const createValidationError = (
  type: ValidationErrorTypeValue,
  message: string,
  field: string,
  itemId: string | null = null,
): ValidationError => ({
  type,
  message,
  field,
  itemId,
});

const createValidationWarning = (message: string, itemId: string | null = null): ValidationWarning => ({
  message,
  itemId,
});

export const validateOrderItems = (items: OrderItem[]): ValidationResult => {
  const errors: ValidationError[] = [];
  const warnings: ValidationWarning[] = [];
  const itemsWithMissingDimensions: ItemWithMissingDimensions[] = [];

  if (!items || items.length === 0) {
    errors.push(createValidationError(ValidationErrorType.NO_ITEMS, 'Order has no items to pack', 'items'));
    return { isValid: false, errors, warnings, itemsWithMissingDimensions };
  }

  items.forEach((item, index) => {
    const itemId = item.sku ?? item.orderItemId ?? item.fulfillmentPlanItemId ?? `item-${index}`;
    const itemName = item.name ?? itemId;
    const length = item.length ?? item.product?.length ?? 0;
    const width = item.width ?? item.product?.width ?? 0;
    const height = item.height ?? item.product?.height ?? 0;

    const hasDimensions = length > 0 && width > 0 && height > 0;

    if (!hasDimensions) {
      itemsWithMissingDimensions.push({
        id: itemId,
        name: itemName,
        sku: item.sku,
      });
      errors.push(
        createValidationError(ValidationErrorType.MISSING_DIMENSIONS, `Item "${itemName}" is missing dimension data`, 'dimensions', itemId),
      );
    }

    if (length < 0 || width < 0 || height < 0) {
      errors.push(
        createValidationError(
          ValidationErrorType.INVALID_DIMENSIONS,
          `Item "${itemName}" has invalid negative dimensions`,
          'dimensions',
          itemId,
        ),
      );
    }

    const weight = typeof item.weight === 'object' ? (item.weight?.value ?? 0) : (item.weight ?? 0);
    if (weight < 0) {
      errors.push(
        createValidationError(ValidationErrorType.INVALID_DIMENSIONS, `Item "${itemName}" has invalid negative weight`, 'weight', itemId),
      );
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    itemsWithMissingDimensions,
  };
};

export const validatePackages = (packages: SellerPackage[]): ValidationResult => {
  const errors: ValidationError[] = [];
  const warnings: ValidationWarning[] = [];

  if (!packages || packages.length === 0) {
    errors.push(createValidationError(ValidationErrorType.NO_PACKAGES, 'No packages available for calculation', 'packages'));
    return { isValid: false, errors, warnings };
  }

  packages.forEach((pkg, index) => {
    const pkgId = pkg.packageId ?? pkg.name ?? `package-${index}`;

    if (!pkg.length || !pkg.width || !pkg.height) {
      warnings.push(createValidationWarning(`Package "${pkg.name ?? pkgId}" is missing dimensions`, pkgId));
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
};

const transformItem = (item: OrderItem, index: number): PackingItem => {
  const lengthValue = item.length ?? item.product?.length ?? 1;
  const widthValue = item.width ?? item.product?.width ?? 1;
  const heightValue = item.height ?? item.product?.height ?? 1;
  const weightValue =
    typeof item.weight === 'object' ? (item.weight?.value ?? item.product?.weight ?? 1) : (item.weight ?? item.product?.weight ?? 1);
  const requirement = normalizePackagingRequirement(item.packagingRequirement);

  return {
    id: `${item.sku ?? item.orderItemId ?? item.fulfillmentPlanItemId ?? 'item'}-${index}`,
    name: item.name ?? item.sku ?? `Item ${index + 1}`,
    length: Math.max(0.1, Number(lengthValue) || 1),
    width: Math.max(0.1, Number(widthValue) || 1),
    height: Math.max(0.1, Number(heightValue) || 1),
    weight: Math.max(0.01, Number(weightValue) || 1),
    fragile: requirement === FRAGILE_REQUIREMENT,
    shipAlone: requirement === SHIP_ALONE_REQUIREMENT,
  };
};

const extractWeight = (weight: WeightValue | undefined): number => {
  if (weight == null) return 0;
  if (typeof weight === 'number') return weight;
  return Number(weight.value) || 0;
};

const getPackageDimensions = (
  pkg: SellerPackage,
): { length: number; width: number; height: number; maxWeight: number; tareWeight: number; weightBuffer: number } => {
  const dimensions = pkg.dimensions ?? {};

  const length = Number(dimensions.length) || Number(pkg.length) || Number(pkg.innerLength) || 0;
  const width = Number(dimensions.width) || Number(pkg.width) || Number(pkg.innerWidth) || 0;
  const height = Number(dimensions.height) || Number(pkg.height) || Number(pkg.innerHeight) || 0;
  const maxWeight = extractWeight(pkg.maxWeight) || Number(pkg.weightCapacity) || Number(pkg.weightLimit) || 0;
  const tareWeight = extractWeight(pkg.tareWeight);
  const weightBuffer = extractWeight(pkg.weightBuffer);

  return { length, width, height, maxWeight, tareWeight, weightBuffer };
};

const transformPackage = (pkg: SellerPackage): PackingBox => {
  const pkgName = pkg.name ?? pkg.packageTypeName ?? 'Package';
  const dims = getPackageDimensions(pkg);

  return {
    name: pkgName,
    packageId: pkg.packageId,
    length: Math.max(0, dims.length),
    width: Math.max(0, dims.width),
    height: Math.max(0, dims.height),
    maxWeight: Math.max(0, dims.maxWeight),
    tareWeight: Math.max(0, dims.tareWeight),
    weightBuffer: Math.max(0, dims.weightBuffer),
    price: Number(pkg.cost) || Number(pkg.price) || 0,
  };
};

const hasValidDimensions = (pkg: SellerPackage): boolean => {
  const dims = getPackageDimensions(pkg);
  return dims.length > 0 && dims.width > 0 && dims.height > 0;
};

// Drop a box only when a max weight is actually configured and tare + buffer leaves no room for
// items. A box with no max weight configured is left in — the engine treats it as inert rather than
// rejecting the request. Must stay identical to the equivalent guard in the backend payload builder
// (PackingRecommendationService.BuildAvailableBoxesPayload).
const hasWeightCapacityForItems = (pkg: SellerPackage): boolean => {
  const dims = getPackageDimensions(pkg);
  return !(dims.maxWeight > 0 && dims.tareWeight + dims.weightBuffer >= dims.maxWeight);
};

export const transformOrderToPackingRequest = (order: Order, packages: SellerPackage[], options: TransformOptions = {}): PackingRequest => {
  const {
    optimizationFactor = DEFAULT_OPTIMIZATION_FACTOR,
    enabledAlgorithms = ['greedy-first-fit', 'greedy-best-fit', 'first-fit-decreasing', 'best-fit-decreasing', 'skyline-algorithm'],
    sourceDimensionUnit = 'Inches',
    sourceWeightUnit = 'Ounces',
  } = options;

  const items: PackingItem[] = [];
  const orderItems = order.items ?? order.orderItems ?? [];

  orderItems.forEach((item) => {
    const quantity = item.quantity ?? 1;
    for (let i = 0; i < quantity; i++) {
      items.push(transformItem(item, items.length));
    }
  });

  const visiblePackages = (packages ?? []).filter((pkg) => !pkg.isHidden);

  let availableBoxes = visiblePackages
    .filter((pkg) => hasValidDimensions(pkg) && hasWeightCapacityForItems(pkg))
    .map((pkg) => transformPackage(pkg));

  const uniqueBoxes: PackingBox[] = [];
  const seenDimensions = new Set<string>();
  availableBoxes.forEach((box) => {
    const key = `${box.name}-${box.length}-${box.width}-${box.height}`;
    if (!seenDimensions.has(key)) {
      seenDimensions.add(key);
      uniqueBoxes.push(box);
    }
  });
  availableBoxes = uniqueBoxes;

  return {
    items,
    availableBoxes,
    optimizationFactor,
    enabledAlgorithms,
    dimensionUnit: sourceDimensionUnit.toLowerCase(),
    weightUnit: sourceWeightUnit.toLowerCase(),
  };
};

export const validateAndTransform = (
  order: Order,
  packages: SellerPackage[],
  options: TransformOptions = {},
): { validation: ValidationResult; request: PackingRequest | null } => {
  const orderItems = order.items ?? order.orderItems ?? [];

  const itemValidation = validateOrderItems(orderItems);
  const packageValidation = validatePackages(packages);

  const validation: ValidationResult = {
    isValid: itemValidation.isValid && packageValidation.isValid,
    errors: [...itemValidation.errors, ...packageValidation.errors],
    warnings: [...itemValidation.warnings, ...packageValidation.warnings],
    itemsWithMissingDimensions: itemValidation.itemsWithMissingDimensions,
  };

  if (!validation.isValid) {
    return { validation, request: null };
  }

  const request = transformOrderToPackingRequest(order, packages, options);

  if (request.availableBoxes.length === 0) {
    validation.isValid = false;
    validation.errors.push(
      createValidationError(ValidationErrorType.NO_PACKAGES, 'No packages with valid dimensions available for calculation', 'packages'),
    );
    return { validation, request: null };
  }

  return { validation, request };
};
